import _ from 'lodash'
import { Mesh, MeshHierarchyStructure, MeshOptions } from 'santa-mesh/cjs/src/Types' // eslint-disable-line no-restricted-syntax
import { Structure, Style } from '@wix/thunderbolt-becky-types'
import { MeshResult } from '../../types/css'
import { DataForMeshCalculation, MeshComponent, OverridesData } from './mesh'
import { POSITION_ABSOLUTE_IN_MESH, skinPartId } from './meshConstants'
import { structure2mesh } from 'santa-mesh'
import { getMeshOverrides } from './meshOverrides'
import { getMeshStyles } from './meshStyle'

export const getStylesAndWedgesAndRotated = (
	compId: string,
	meshData: Mesh,
	isChildOfRepeater = false,
	isHoverBox = false
): MeshResult => {
	const children = _.get(meshData, 'structure.children[0].children[0].children', []) as Array<MeshHierarchyStructure>
	const wedges = children.map((child) => child.id).filter((id) => id.includes('wedge'))
	const rotatedComponents = children
		.filter((child) => child.id.includes('rotated-wrapper'))
		.map((rotatedWrapper) => rotatedWrapper.children[0].id)
	const stylesMap = getMeshStyles(compId, meshData.styles, rotatedComponents, isChildOfRepeater, isHoverBox)
	return { stylesMap, wedges, rotatedComponents }
}

const getMeshContainer = (
	compMeshData: DataForMeshCalculation,
	components: Array<MeshComponent>,
	overridesData: OverridesData
) => {
	const { compStructure, compData, dataForColumn = {} } = compMeshData
	const { layout, metaData: compMetaData } = compStructure
	const { props: compProp = {}, style = {}, data = {} } = compData
	const compSkin = (style as Style).skin
	const skinName = (compSkin || compStructure.skin) as string
	const filteredComps = components.filter((child) => !child.isFixed)
	const overrides = getMeshOverrides({
		...overridesData,
		compProp,
		compData: data,
		dataForColumn,
		components: filteredComps,
		componentType: compStructure.componentType,
		compId: compStructure.id,
		compMetaData,
		skinName,
	})
	return {
		width: layout.width || 0,
		height: layout.height,
		id: `${compStructure.id}${skinPartId}`,
		components: filteredComps,
		adjustingComp: null,
		...overrides,
	}
}

const removeMeshGridWrapper = (meshData: Mesh): Mesh => {
	const gridWrapperId = _.get(meshData.structure, ['children', '0', 'id'])

	if (!gridWrapperId) {
		// empty container
		return meshData
	}

	_.assign(meshData.styles[meshData.structure.id], meshData.styles[gridWrapperId])
	delete meshData.styles[gridWrapperId]

	return meshData
}

export const getMeshData = (
	compMeshData: DataForMeshCalculation,
	childComponents: Array<MeshComponent>,
	overridesData: OverridesData,
	meshOptions: MeshOptions = {}
): Mesh => {
	const container = getMeshContainer(compMeshData, childComponents, overridesData)
	// @ts-ignore - Remove once santa-mesh is updated
	const meshData = structure2mesh(container, meshOptions)
	return removeMeshGridWrapper(meshData)
}

export const getMeshComponents = (compId: string, structure: Structure): Array<MeshComponent> => {
	const compStructure = structure[compId]

	return (compStructure.components || []).map((childCompId) => {
		const childStructure = structure[childCompId]
		const { x, y, width, height, docked, fixedPosition, rotationInDegrees } = childStructure.layout

		return {
			id: childCompId,
			left: x,
			top: y,
			width,
			height,
			docked: docked || {},
			isFixed: Boolean(fixedPosition),
			rotationInDegrees,
			absolute: POSITION_ABSOLUTE_IN_MESH.includes(childStructure.componentType),
		} as MeshComponent
	})
}

export const getMeshResult = (compMeshData: DataForMeshCalculation, overridesData: OverridesData): MeshResult => {
	const compId = compMeshData.compStructure.id
	const childComponents = getMeshComponents(compId, compMeshData.compAndChildrenStructure!)
	const fixedComponents = childComponents.filter((comp) => comp.isFixed).map((comp) => comp.id)
	return {
		fixedComponents,
		...getStylesAndWedgesAndRotated(
			compId,
			getMeshData(compMeshData, childComponents, overridesData),
			compMeshData.isChildOfRepeater
		),
	}
}
